// api/truth-set.ts
//
// Truth set (backend app/services/truth_set): what is true about a brand, read
// from its own site, confirmed by a person, checked against every AI answer.
// Part 1 is the site read: the brand's domain (robots.txt, sitemaps, llms.txt,
// pages under the domain within a page cap) plus uploaded documents. Admin-only.

import { apiFetch } from "./api-fetch";

export type SiteReadStatus = "pending" | "running" | "completed" | "cancelled" | "failed";
export type SiteReadStop = "done" | "page_cap" | "time_cap" | "cancelled" | "error";

export interface SiteConfig {
  root_url: string | null;
  root_error?: string;
  page_cap: number;
  time_cap_sec: number;
  delay_sec: number;
  cadence_days: number;
  schedule_enabled: boolean;
  exclude_patterns: string[];
}

export interface SiteRead {
  id: number;
  customer_id: number;
  trigger: "manual" | "scheduled" | "onboarding" | "intake";
  root_url: string;
  status: SiteReadStatus;
  stop_reason: SiteReadStop | null;
  cancel_requested: boolean;
  is_baseline: boolean;
  elapsed_sec: number;
  pages_fetched: number;
  pages_new: number;
  pages_changed: number;
  pages_unchanged: number;
  pages_gone: number;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  config_json?: Partial<SiteConfig>;
  report_json?: SiteReadReport | null;
}

export interface SiteReadUrl {
  id: number;
  url: string;
  source: string;
  status: "queued" | "fetched" | "duplicate" | "skipped" | "blocked" | "error";
  reason: string | null;
  http_status: number | null;
  change_kind: string | null;
}

export interface SiteReadReport {
  stop_reason: SiteReadStop;
  baseline: boolean;
  pages_read: number;
  page_cap: number;
  elapsed_sec: number;
  time_cap_sec: number;
  urls: Record<string, number>;
  changes: Record<"baseline" | "new" | "changed" | "unchanged" | "gone", number>;
  notes: string[];
  samples: Record<"errors" | "blocked" | "skipped", SiteReadUrl[]>;
}

export interface SiteChange {
  id: number;
  read_id: number;
  page_id: number;
  url: string;
  kind: "new" | "changed" | "gone";
  old_hash: string | null;
  new_hash: string | null;
  lines_added: number;
  lines_removed: number;
  diff_truncated: boolean;
  created_at: string;
  diff?: string | null;
}

export interface TruthDocument {
  id: number;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sha256: string;
  text_chars: number;
  uploaded_by: string | null;
  uploaded_at: string;
  removed_at: string | null;
  duplicate?: boolean;
}

export interface SiteOverview {
  available: boolean;
  config?: SiteConfig;
  scheduled?: boolean;
  reads?: SiteRead[];
  pages?: { live: number };
  documents?: TruthDocument[];
  changes?: SiteChange[];
}

export interface SiteReadDetail extends SiteRead {
  live_counts: { urls: Record<string, number>; changes: Record<string, number> };
  changes: SiteChange[];
}

const customerBase = (customerId: number) => `/truth-set/customers/${customerId}`;

export function getSiteOverview(customerId: number): Promise<SiteOverview> {
  return apiFetch(`${customerBase(customerId)}/site`);
}

export function updateSiteConfig(customerId: number, body: Partial<SiteConfig>): Promise<SiteConfig> {
  return apiFetch(`${customerBase(customerId)}/site/config`, { method: "PUT", body: JSON.stringify(body) });
}

export function launchSiteRead(
  customerId: number, body: { page_cap?: number; time_cap_sec?: number } = {},
): Promise<SiteRead> {
  return apiFetch(`${customerBase(customerId)}/site-reads`, { method: "POST", body: JSON.stringify(body) });
}

export function getSiteRead(readId: number): Promise<SiteReadDetail> {
  return apiFetch(`/truth-set/site-reads/${readId}`);
}

export function listSiteReadUrls(readId: number, status?: string): Promise<SiteReadUrl[]> {
  return apiFetch(`/truth-set/site-reads/${readId}/urls${status ? `?status=${encodeURIComponent(status)}` : ""}`);
}

export function cancelSiteRead(readId: number, cancel = true): Promise<SiteRead> {
  return apiFetch(`/truth-set/site-reads/${readId}/cancel`, { method: "POST", body: JSON.stringify({ cancel }) });
}

export function resumeSiteRead(
  readId: number, body: { page_cap?: number; time_cap_sec?: number } = {},
): Promise<SiteRead> {
  return apiFetch(`/truth-set/site-reads/${readId}/resume`, { method: "POST", body: JSON.stringify(body) });
}

export function getSiteChange(changeId: number): Promise<SiteChange> {
  return apiFetch(`/truth-set/site-changes/${changeId}`);
}

export function uploadTruthDocument(customerId: number, file: File): Promise<TruthDocument> {
  const form = new FormData();
  form.append("file", file);
  return apiFetch(`${customerBase(customerId)}/documents`, { method: "POST", body: form });
}

export function removeTruthDocument(documentId: number): Promise<TruthDocument> {
  return apiFetch(`/truth-set/documents/${documentId}`, { method: "DELETE" });
}

export const STOP_LABELS: Record<SiteReadStop, string> = {
  done: "Read the whole site",
  page_cap: "Stopped at the page cap",
  time_cap: "Stopped at the time cap",
  cancelled: "Cancelled",
  error: "Failed",
};

// ── Part 2: fact extraction ──────────────────────────────────────────────────
//
// A judge pass over every stored page and document proposes facts. Each fact
// is backed by sentences found verbatim on the brand's own sources. Facts that
// disagree on a single-valued attribute are an internal conflict and are listed
// first. Nothing here is confirmed yet.

export interface TruthAttribute {
  key: string;
  label: string;
  description: string;
  cardinality: "one" | "many";
  source: string;
}

export interface CustomAttribute {
  id: number;
  customer_id: number | null;
  vertical: string | null;
  key: string;
  label: string;
  description: string;
  cardinality: "one" | "many";
  created_by: string | null;
}

export interface ProposedFact {
  id: number;
  subject_type: "brand" | "product" | "person" | "location";
  subject_name: string;
  attribute: string;
  attribute_label: string;
  qualifier: string;
  value: string;
  value_norm: string;
  status: "proposed" | "confirmed" | "corrected" | "removed";
  in_conflict: boolean;
  source_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

export interface FactSource {
  id: number;
  source_kind: "page" | "document";
  source_label: string;
  seen_at: string | null;
  value: string;
  qualifier: string;
  sentence: string;
}

export interface ConflictGroup {
  conflict_key: string;
  attribute: string;
  subject_name: string;
  subject_type: string;
  qualifier: string;
  facts: ProposedFact[];
}

export interface ExtractionRun {
  id: number;
  trigger: "manual" | "site_read" | "scheduled";
  model: string;
  force: boolean;
  status: SiteReadStatus;
  stop_reason: "done" | "spend_cap" | "cancelled" | "error" | null;
  cancel_requested: boolean;
  spend_cap_usd: number;
  cost_usd: number;
  sources_total: number;
  sources_done: number;
  sources_reused: number;
  sources_failed: number;
  error_message: string | null;
  created_at: string;
  finished_at: string | null;
  report_json?: {
    facts?: { facts: number; conflicts: number; claims: number; claims_verified: number; facts_no_longer_stated: number } | null;
    attribute_proposal?: { types: string[]; pages: number; cost_usd: number } | null;
    failed_sources?: { item_id: number; source: string | null; error: string | null }[];
    unverified_sample?: { id: number; source_label: string; attribute: string; value: string; sentence: string }[];
    truncated_sources?: number[];
  } | null;
}

export interface FactsOverview {
  available: boolean;
  model?: string;
  vertical?: string;
  verticals?: { key: string; label: string }[];
  vocabulary?: TruthAttribute[];
  custom_attributes?: CustomAttribute[];
  sources_needing_extraction?: number;
  runs?: ExtractionRun[];
  live_run?: ExtractionRun | null;
  conflicts?: ConflictGroup[];
  facts?: ProposedFact[];
}

export function getFactsOverview(customerId: number): Promise<FactsOverview> {
  return apiFetch(`${customerBase(customerId)}/facts`);
}

export function setVertical(customerId: number, vertical: string): Promise<FactsOverview> {
  return apiFetch(`${customerBase(customerId)}/vertical`, { method: "PUT", body: JSON.stringify({ vertical }) });
}

export function addTruthAttribute(
  customerId: number,
  body: { key: string; label: string; description: string; cardinality: "one" | "many"; scope: "brand" | "vertical" },
): Promise<FactsOverview> {
  return apiFetch(`${customerBase(customerId)}/attributes`, { method: "POST", body: JSON.stringify(body) });
}

export function removeTruthAttribute(customerId: number, attributeId: number): Promise<FactsOverview> {
  return apiFetch(`${customerBase(customerId)}/attributes/${attributeId}`, { method: "DELETE" });
}

export function launchExtraction(
  customerId: number, body: { force?: boolean; spend_cap_usd?: number } = {},
): Promise<ExtractionRun> {
  return apiFetch(`${customerBase(customerId)}/extractions`, { method: "POST", body: JSON.stringify(body) });
}

export function cancelExtraction(runId: number, cancel = true): Promise<ExtractionRun> {
  return apiFetch(`/truth-set/extractions/${runId}/cancel`, { method: "POST", body: JSON.stringify({ cancel }) });
}

export function resumeExtraction(runId: number, spend_cap_usd?: number): Promise<ExtractionRun> {
  return apiFetch(`/truth-set/extractions/${runId}/resume`, {
    method: "POST", body: JSON.stringify(spend_cap_usd === undefined ? {} : { spend_cap_usd }),
  });
}

export function getFact(factId: number): Promise<ProposedFact & { sources: FactSource[] }> {
  return apiFetch(`/truth-set/facts/${factId}`);
}

// ── Part 3: confirmation and versioning ──────────────────────────────────────
//
// One set of endpoints for the admin (Stage 3) and the client (Deep Signal):
// the server decides the role from the viewer. A person decides every fact,
// then confirms the set as a numbered version that can never be edited. Only
// confirmed versions are used to check AI answers.

export type FactAction = "confirm" | "correct" | "remove" | "reset";

export interface TruthFact {
  id: number;
  subject_type: "brand" | "product" | "person" | "location";
  subject_name: string;
  attribute: string;
  attribute_label: string;
  qualifier: string;
  value: string;
  status: "proposed" | "confirmed" | "corrected" | "removed";
  origin: "site" | "manual";
  truth_value: string | null;
  decided_role: "admin" | "client" | null;
  decided_by?: string | null;
  decided_at: string | null;
  decision_note: string | null;
  active: boolean;
  in_conflict: boolean;
  source_count: number;
  last_seen_at: string | null;
  sources: { source_label: string; sentence: string; seen_at: string | null }[];
}

// One attribute and value, across every subject and qualifier stating it: the
// unit a person reviews. A group's decision goes to every fact in it.
export interface TruthFactGroup {
  attribute: string;
  attribute_label: string;
  value_norm: string;
  value: string;
  fact_ids: number[];
  member_count: number;
  subjects: string[];
  source_count: number;
  undecided: number;
  in_conflict: number;
}

// The kept facts by subject and attribute, across every qualifier: what the
// set will say, read together before it is locked.
export interface KeptFactGroup {
  subject_type: string;
  subject_name: string;
  attribute: string;
  attribute_label: string;
  values: { value: string; qualifier: string }[];
  line: string;
}

export interface TruthSetChange {
  op: "add" | "remove" | "change";
  attribute: string;
  attribute_label?: string;
  subject_type: string;
  subject_name: string;
  qualifier: string;
  from: string | null;
  to: string | null;
}

export interface TruthSetVersion {
  id: number;
  version_number: number;
  context: "onboarding" | "update" | "rebaseline";
  fact_count: number;
  content_hash: string;
  note: string;
  confirmed_by: string;
  confirmed_role: "admin" | "client";
  confirmed_at: string;
  lines: string[];
  facts?: { attribute_label: string; subject_name: string; subject_type: string; qualifier: string; value: string;
            status: string; origin: string }[];
}

export interface TruthSetView {
  available: boolean;
  role?: "admin" | "client";
  brand?: string;
  vocabulary?: { key: string; label: string; cardinality: "one" | "many" }[];
  facts?: TruthFact[];
  value_groups?: TruthFactGroup[];
  groups?: KeptFactGroup[];
  counts?: Record<"proposed" | "confirmed" | "corrected" | "removed" | "manual" | "no_longer_on_site", number>;
  latest_version?: TruthSetVersion | null;
  versions?: TruthSetVersion[];
  pending_changes?: TruthSetChange[];
  pending_lines?: string[];
  blockers?: string[];
  can_confirm?: boolean;
  onboarding_skip?: { skip_reason: string; skipped_by: string; skipped_at: string } | null;
}

const brandBase = (customerId: number) => `/truth-set/brands/${customerId}`;

export function getTruthSet(customerId: number): Promise<TruthSetView> {
  return apiFetch(brandBase(customerId));
}

export function decideFact(
  customerId: number, factId: number, body: { action: FactAction; value?: string; note?: string },
): Promise<TruthSetView> {
  return apiFetch(`${brandBase(customerId)}/facts/${factId}/decision`, { method: "POST", body: JSON.stringify(body) });
}

export function decideFactGroup(
  customerId: number,
  body: { attribute: string; value_norm: string; action: FactAction; value?: string; note?: string },
): Promise<TruthSetView> {
  return apiFetch(`${brandBase(customerId)}/facts/group-decision`, { method: "POST", body: JSON.stringify(body) });
}

export function addManualFact(
  customerId: number,
  body: { attribute: string; value: string; subject_type?: string; subject?: string; qualifier?: string; note?: string },
): Promise<TruthSetView> {
  return apiFetch(`${brandBase(customerId)}/facts`, { method: "POST", body: JSON.stringify(body) });
}

export function confirmTruthSet(
  customerId: number, body: { note: string; expected_version_id: number | null },
): Promise<TruthSetVersion> {
  return apiFetch(`${brandBase(customerId)}/versions`, { method: "POST", body: JSON.stringify(body) });
}

export function getTruthSetVersion(customerId: number, versionId: number): Promise<TruthSetVersion> {
  return apiFetch(`${brandBase(customerId)}/versions/${versionId}`);
}

export function setOnboardingSkip(customerId: number, reason: string | null): Promise<TruthSetView> {
  return apiFetch(`/truth-set/customers/${customerId}/onboarding-skip`, {
    method: "PUT", body: JSON.stringify({ reason }),
  });
}

// ── Part 4: checking reads ───────────────────────────────────────────────────

export interface TruthCheckRun {
  id: number;
  run_id: number;
  version_id: number;
  trigger: "auto" | "manual";
  model: string;
  status: SiteReadStatus;
  stop_reason: "done" | "spend_cap" | "cancelled" | "error" | null;
  cancel_requested: boolean;
  spend_cap_usd: number;
  cost_usd: number;
  answers_total: number;
  answers_checked: number;
  answers_failed: number;
  error_message: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface RunChecks {
  checks: TruthCheckRun[];
  latest_version: number | null;
  latest_version_id: number | null;
  stale: boolean;
  live: TruthCheckRun | null;
}

export function getRunChecks(customerId: number, runId: number): Promise<RunChecks> {
  return apiFetch(`${customerBase(customerId)}/runs/${runId}/checks`);
}

export function launchRunCheck(customerId: number, runId: number, spend_cap_usd?: number): Promise<TruthCheckRun> {
  return apiFetch(`${customerBase(customerId)}/runs/${runId}/checks`, {
    method: "POST", body: JSON.stringify(spend_cap_usd === undefined ? {} : { spend_cap_usd }),
  });
}

export function cancelRunCheck(checkId: number, cancel = true): Promise<TruthCheckRun> {
  return apiFetch(`/truth-set/checks/${checkId}/cancel`, { method: "POST", body: JSON.stringify({ cancel }) });
}

export function resumeRunCheck(checkId: number): Promise<TruthCheckRun> {
  return apiFetch(`/truth-set/checks/${checkId}/resume`, { method: "POST", body: JSON.stringify({}) });
}

// ── Global Settings: truth set automation ──────────────────────────────────

export interface TruthAutomationSettings { enabled: boolean; run_cap_usd: number; monthly_cap_usd: number }
export interface TruthAutomationView {
  settings: TruthAutomationSettings;
  defaults: TruthAutomationSettings;
  month: { spent: number; committed: number } | null;
}

export function getTruthAutomation(): Promise<TruthAutomationView> {
  return apiFetch(`/truth-set/settings/automation`);
}

export function saveTruthAutomation(values: Partial<TruthAutomationSettings>): Promise<TruthAutomationView> {
  return apiFetch(`/truth-set/settings/automation`, { method: "PUT", body: JSON.stringify({ values }) });
}
